import React, { useState } from 'react';
import { Building2, Users, Award, CheckCircle, ClipboardCheck, Ambulance, Microscope, Eye, Ear, Heart, Truck, Shield, Clock, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CorporateHealth = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    package_type: '',
    employee_count: '',
    message: '',
  });

  const challenges = [
    {
      icon: Users,
      title: 'Unplanned Absenteeism',
      description: 'Proactively identify health risks before they escalate into sick leave and workforce disruption.',
    },
    {
      icon: Shield,
      title: 'Regulatory Risks',
      description: '100% compliance with statutory industrial health requirements — avoid penalties.',
    },
    {
      icon: Clock,
      title: 'Operational Downtime',
      description: '24/7 medical support aligned with your production shifts for continuous operations.',
    },
    {
      icon: ClipboardCheck,
      title: 'Hiring Delays',
      description: 'Rapid pre-employment screening to accelerate candidate onboarding without compromising quality.',
    },
  ];

  const coreServices = [
    {
      icon: ClipboardCheck,
      title: 'Pre-Employment Screening',
      description: 'Comprehensive "fit-for-work" evaluations for new candidates',
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Users,
      title: 'Annual Medical Exams',
      description: 'Scheduled health monitoring for your entire existing workforce',
      color: 'text-green-500',
      bgColor: 'bg-green-50',
    },
    {
      icon: Building2,
      title: 'OHC Management',
      description: 'Complete setup and staffing of onsite Occupational Health Centers',
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
    },
    {
      icon: Ambulance,
      title: 'Emergency Support',
      description: '24/7 BLS Ambulance services with trained medical drivers',
      color: 'text-red-500',
      bgColor: 'bg-red-50',
    },
  ];

  const diagnosticCapabilities = [
    { icon: Microscope, title: 'Radiology', description: 'High-Frequency Digital X-Ray and 4D Ultrasound systems' },
    { icon: Heart, title: 'Cardiac & Lung Health', description: 'Computerized ECG and Pulmonary Function Testing (PFT)' },
    { icon: Award, title: 'Laboratory Excellence', description: '3,500+ specialized tests using fully automated analyzers' },
    { icon: Eye, title: 'Specialized Screening', description: 'Audiometry (Hearing) and comprehensive Eye examinations' },
  ];

  const executionModels = [
    {
      title: 'Clinic Walk-In (Offsite)',
      description: 'Ideal for individual and pre-employment checks',
      features: ['Fast-track service at our 24/7 facility', 'Individual and pre-employment checks', 'Immediate report generation'],
      color: 'border-[#0EA5E9]',
    },
    {
      title: 'Mobile Medical Camps (Onsite)',
      description: 'Zero Disruption Guarantee — we come to you',
      features: ['Specialized medical teams visit your factory or office', 'Batch-based scheduling for zero disruption', 'Complete diagnostic setup on-site'],
      color: 'border-amber-500',
    },
  ];

  const ohcServices = [
    'Expert Staffing — Qualified MBBS Doctors and certified Staff Nurses',
    'Staff Continuity — Guaranteed alternate personnel during leave periods',
    'Digital Medical Records — Employee health records and lifestyle risk monitoring',
    'Pharmacy Management — Complete OHC medicine supplies and inventory oversight',
  ];

  const whyAMC = [
    { title: '24/7 Operational Support', description: 'Round-the-clock support for all shift patterns' },
    { title: 'Quality Benchmarks', description: 'Operating under NABL M(EL)T program standards for laboratory excellence' },
    { title: 'Rapid Reporting', description: 'Detailed medical reports delivered to HR/HSE within 7-10 working days' },
    { title: 'Local Expertise', description: 'Strategically located to serve industrial hubs of North Chennai' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, package_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.company_name || !formData.contact_person || !formData.email || !formData.phone || !formData.package_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const payload = { ...formData, employee_count: parseInt(formData.employee_count) || 0 };
      await axios.post(`${API}/corporate`, payload);
      toast.success('Thank you! We will contact you shortly to discuss your corporate health needs.');
      setFormData({ company_name: '', contact_person: '', email: '', phone: '', package_type: '', employee_count: '', message: '' });
    } catch (error) {
      console.error('Error submitting corporate inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <section className="bg-gradient-to-r from-[#0A2540] to-[#0EA5E9] py-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <Building2 className="h-16 w-16 text-white mx-auto mb-4" />
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="corporate-health-heading"
          >
            Corporate Health & Wellness
          </h1>
          <p className="text-xl text-white/90 mb-2">Strategic Proposal for Your Business</p>
          <p className="text-lg text-white/70 max-w-3xl mx-auto">
            Ensuring Workforce Productivity through Professional Occupational Health Management.
            A healthcare partner that works as hard as your business does.
          </p>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Challenges We Solve
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {challenges.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                  <Icon className="h-10 w-10 text-[#0EA5E9] mb-4" />
                  <h3 className="text-lg font-bold text-[#0A2540] mb-2">{item.title}</h3>
                  <p className="text-[#475569] text-sm">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Core Service Portfolio
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreServices.map((service) => {
              const Icon = service.icon;
              return (
                <div key={service.title} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-[#E2E8F0]" data-testid={`core-service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className={`${service.bgColor} rounded-full w-16 h-16 flex items-center justify-center mb-4`}>
                    <Icon className={`h-8 w-8 ${service.color}`} />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A2540] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{service.title}</h3>
                  <p className="text-[#475569]">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Advanced Diagnostic Capabilities
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {diagnosticCapabilities.map((cap) => {
              const Icon = cap.icon;
              return (
                <div key={cap.title} className="bg-white rounded-xl p-6 shadow-md text-center">
                  <Icon className="h-10 w-10 text-[#0EA5E9] mx-auto mb-3" />
                  <h3 className="font-bold text-[#0A2540] mb-2">{cap.title}</h3>
                  <p className="text-[#475569] text-sm">{cap.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Flexible Execution Models
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {executionModels.map((model) => (
              <div key={model.title} className={`bg-white rounded-2xl p-8 shadow-lg border-l-4 ${model.color}`}>
                <h3 className="text-2xl font-bold text-[#0A2540] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{model.title}</h3>
                <p className="text-[#475569] mb-4 font-medium">{model.description}</p>
                <ul className="space-y-2">
                  {model.features.map((feature) => (
                    <li key={feature} className="flex items-start text-sm text-[#475569]">
                      <CheckCircle className="h-5 w-5 text-[#0EA5E9] mr-2 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Professional OHC Management
          </h2>
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <ul className="space-y-4">
              {ohcServices.map((service) => (
                <li key={service} className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-[#0EA5E9] mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-[#0A2540] font-medium text-lg">{service}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gradient-to-r from-[#0A2540] to-[#0EA5E9]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why Aaric Medical Center?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyAMC.map((item) => (
              <div key={item.title} className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-white font-bold mb-2">{item.title}</h3>
                <p className="text-white/80 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
            <h2 className="text-3xl font-bold text-[#0A2540] mb-2 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Request a Corporate Health Package
            </h2>
            <p className="text-center text-[#475569] mb-8">
              Fill in the details below and our team will contact you to customize a package for your organization
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input id="company_name" name="company_name" value={formData.company_name} onChange={handleInputChange} required className="mt-1" data-testid="corporate-company-name-input" />
                </div>
                <div>
                  <Label htmlFor="contact_person">Contact Person *</Label>
                  <Input id="contact_person" name="contact_person" value={formData.contact_person} onChange={handleInputChange} required className="mt-1" data-testid="corporate-contact-person-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required className="mt-1" data-testid="corporate-email-input" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input id="phone" name="phone" value={formData.phone} onChange={handleInputChange} required className="mt-1" data-testid="corporate-phone-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="package_type">Service Required *</Label>
                  <Select onValueChange={handleSelectChange} value={formData.package_type}>
                    <SelectTrigger className="mt-1" data-testid="corporate-package-select">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pre-employment">Pre-Employment Screening</SelectItem>
                      <SelectItem value="annual-medical">Annual Medical Exams</SelectItem>
                      <SelectItem value="ohc-management">OHC Management</SelectItem>
                      <SelectItem value="emergency-support">Emergency Support (BLS Ambulance)</SelectItem>
                      <SelectItem value="mobile-camp">Mobile Medical Camp (Onsite)</SelectItem>
                      <SelectItem value="custom">Custom Package</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="employee_count">Number of Employees</Label>
                  <Input id="employee_count" name="employee_count" type="number" value={formData.employee_count} onChange={handleInputChange} className="mt-1" data-testid="corporate-employee-count-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="message">Additional Requirements</Label>
                <Textarea id="message" name="message" value={formData.message} onChange={handleInputChange} rows={4} className="mt-1" placeholder="Tell us about your specific requirements..." data-testid="corporate-message-textarea" />
              </div>
              <Button type="submit" className="w-full bg-[#0A2540] text-white hover:bg-[#0A2540]/90 rounded-full py-6 text-lg font-semibold" data-testid="submit-corporate-inquiry-button">
                Submit Inquiry
              </Button>
            </form>
          </div>
        </div>
      </section>

      <section className="py-12 px-4 bg-[#F8FAFC] text-center">
        <p className="text-[#475569] mb-3">For immediate assistance, call us now:</p>
        <a href="tel:6380608090" data-testid="corporate-call-now-button">
          <Button className="bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90 rounded-full px-8 py-3 text-lg font-semibold">
            <Phone className="mr-2 h-5 w-5" /> Call Now: 6380608090
          </Button>
        </a>
      </section>
    </div>
  );
};

export default CorporateHealth;
